import { FC, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, Modal, Select, Input, ISelectOption } from '@salecycle/ui-component-library';
import { Ruleset } from 'entities/campaign';
import { getTemplateList, RulesetTemplate } from 'entities/campaign/rulesetTemplates';
import { Selectors as Router } from 'router';
import { Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import { Actions, Selectors as RulesetSelectors } from 'sections/campaigns/state/rulesets';
import { State } from 'store';
import { useTranslation } from 'react-i18next';
import { rulesetNameValidation } from '../progress';
import { useLocation } from 'react-router-dom';
import { Selectors as SiteSelectors } from 'sections/client-management/services/sites';

const StyledNewRuleset = styled.div`
  margin-right: 4rem;
`;

export interface StateProps {
  newRulesetId: string;
  templates: RulesetTemplate[];
  campaignId: string;
  rulesetTypeOption?: ISelectOption<Ruleset>;
}

interface DispatchProps {
  addRuleset: (id: string, ruleset: Ruleset) => void;
}

export type Props = StateProps & DispatchProps;

export const NewRuleset: FC<Props> = props => {
  const { t } = useTranslation('campaigns');
  const location = useLocation<{ showAddNewRulesetModal: boolean }>();
  const showAddNewRulesetModal =
    location.state === undefined || location.state.showAddNewRulesetModal === undefined
      ? false
      : location.state.showAddNewRulesetModal;

  const [isOpen, setIsOpen] = useState(showAddNewRulesetModal);
  const [rulesetType, setRulesetType] = useState<ISelectOption<Ruleset>>(props.rulesetTypeOption || null);
  const [rulesetName, setRulesetName] = useState<string>('');

  const onAddRulesetClick = () => {
    setIsOpen(!isOpen);
  };

  const handleRulesetTypeChange = (value: ISelectOption<Ruleset>) => setRulesetType(value);

  const handleRulesetNameChange = (value: string) => setRulesetName(value);

  const isRulesetNameValid = rulesetNameValidation(rulesetName, t).expression;

  const rulesetTypeOptions: ISelectOption<Ruleset>[] = props.templates.map(template => ({
    value: template.ruleset,
    label: template.name,
  }));

  const handleOkClick = () => {
    props.addRuleset(props.newRulesetId, { ...rulesetType.value, name: rulesetName, campaignId: props.campaignId });
    setIsOpen(false);
    setRulesetName('');
    setRulesetType(null);
  };

  if (rulesetTypeOptions.length === 0) {
    return null;
  }

  return (
    <StyledNewRuleset>
      <Button variation="secondaryDark" onClick={onAddRulesetClick}>
        {t('campaigns:editor.rulesets.addRuleset.label')}
      </Button>

      <Modal
        heading={t('campaigns:editor.rulesets.addRuleset.modal.heading')}
        isOpen={isOpen}
        onCloseButtonClicked={() => setIsOpen(!isOpen)}
        minHeight={450}
        onOkButtonClicked={handleOkClick}
        okButtonDisabled={!rulesetType || !isRulesetNameValid}
        okButtonLabel={t('campaigns:editor.rulesets.addRuleset.modal.addButton')}
        variation="clean"
        overflow="visible"
      >
        <Input
          label={t('campaigns:editor.rulesets.addRuleset.modal.rulesetName')}
          ariaLabel={t('campaigns:editor.rulesets.addRuleset.modal.rulesetName')}
          value={rulesetName}
          onChange={handleRulesetNameChange}
          placeholder={t('campaigns:editor.rulesets.addRuleset.modal.rulesetNamePlaceholder')}
          validationMessage={rulesetName && !isRulesetNameValid && rulesetNameValidation(rulesetName, t).message}
          validationState={rulesetName && !isRulesetNameValid && 'warning'}
        />
        <Select
          label={t('campaigns:editor.rulesets.addRuleset.modal.rulesetType')}
          ariaLabel={t('campaigns:editor.rulesets.addRuleset.modal.rulesetType')}
          value={rulesetType}
          options={rulesetTypeOptions}
          onChange={handleRulesetTypeChange}
          placeholder={t('campaigns:editor.rulesets.addRuleset.modal.rulesetTypePlaceholder')}
        />
      </Modal>
    </StyledNewRuleset>
  );
};

export const mapState = (state: State): StateProps => {
  const campaignId = Router.getCampaignId(state);
  const campaign = CampaignSelectors.getEntity(state, campaignId);

  const templates = campaign ? getTemplateList(campaign.type) : [];
  const siteId = Router.getSiteId(state);
  const site = SiteSelectors.getUpdatedEntity(state, siteId);
  // Ensure that the default templateId is set to null and default delay is set to minimum session timeout
  templates.forEach(template => {
    if (template?.ruleset?.channels) {
      template.ruleset.channels.forEach(channel => {
        if (channel?.templateId && channel?.templateId !== '') {
          channel.templateId = null;
        }
        if (channel?.type === '.Email') {
          channel.delay = site.sessionTime;
        }
      });
    }
  });

  return {
    campaignId,
    templates,
    newRulesetId: RulesetSelectors.newRulesetId(state),
  };
};

const actions: DispatchProps = {
  addRuleset: Actions.createLocalWithTemporaryId,
};

export default connect(mapState, actions)(NewRuleset);
